import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import PageSEO from "@/components/feature/PageSEO";
import { articles } from "@/mocks/articles";
import { hotComics } from "@/mocks/comics";
import { siteInfo } from "@/mocks/siteInfo";

export default function ArticleDetail() {
  const { t } = useTranslation();
  const { id } = useParams();
  const article = articles.find((a) => a.id === Number(id)) || articles[0];
  const relatedArticles = articles.filter((a) => a.id !== article.id).slice(0, 3);
  const relatedComics = hotComics.slice(0, 3);

  const breadcrumbItems = [
    { label: t("nav.article"), path: "/article" },
    { label: article.title },
  ];

  const schemas = [
    {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: article.title,
      description: article.excerpt,
      author: { "@type": "Person", name: article.author },
      datePublished: article.date,
      dateModified: article.date,
      image: article.imageUrl,
      publisher: {
        "@type": "Organization",
        name: siteInfo.name,
        logo: { "@type": "ImageObject", url: siteInfo.url + "/vite.svg" },
      },
      mainEntityOfPage: {
        "@type": "WebPage",
        "@id": siteInfo.url + "/article/" + article.id,
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: t("breadcrumb.home"), item: siteInfo.url },
        { "@type": "ListItem", position: 2, name: t("nav.article"), item: siteInfo.url + "/article" },
        { "@type": "ListItem", position: 3, name: article.title },
      ],
    },
  ];

  return (
    <>
      <PageSEO
        title={article.title + " - " + siteInfo.name}
        description={article.excerpt}
        keywords={article.category + "," + article.title + ",韩漫推荐,韩国漫画资讯"}
        canonicalUrl={siteInfo.url + "/article/" + article.id}
        ogType="article"
        ogImage={article.imageUrl}
      />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schemas) }} />
      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          <Breadcrumb items={breadcrumbItems} />

          <article className="px-4 md:px-6 py-8 md:py-12">
            <div className="max-w-4xl mx-auto">
              <div className="mb-6">
                <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-800 text-xs font-medium mb-3">
                  {article.category}
                </span>
                <h1 className="text-2xl md:text-4xl font-bold text-foreground-950 leading-tight">
                  {article.title}
                </h1>
                <div className="mt-4 flex flex-wrap items-center gap-4 text-sm text-foreground-500">
                  <span className="flex items-center gap-1.5">
                    <i className="ri-user-line w-4 h-4 flex items-center justify-center"></i>
                    {article.author}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-calendar-line w-4 h-4 flex items-center justify-center"></i>
                    {article.date}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-time-line w-4 h-4 flex items-center justify-center"></i>
                    {article.readTime}
                  </span>
                </div>
              </div>

              <div className="rounded-2xl overflow-hidden mb-10">
                <img
                  src={article.imageUrl}
                  alt={article.title}
                  title={article.title + " - 在线漫画韩漫"}
                  className="w-full aspect-[16/9] object-cover object-top"
                />
              </div>

              <div
                className="prose prose-lg max-w-none text-foreground-700 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />

              <div className="mt-12 pt-8 border-t border-background-200">
                <div className="flex flex-wrap gap-2">
                  <span className="text-sm text-foreground-500">标签：</span>
                  {["韩漫", article.category, "漫画推荐", "韩国漫画"].map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 rounded-full bg-background-100 text-foreground-600 text-xs font-medium"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </article>

          <section className="px-4 md:px-6 py-8 md:py-12 bg-background-100">
            <div className="max-w-4xl mx-auto">
              <h3 className="text-xl font-bold text-foreground-950 mb-6">
                相关文章
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {relatedArticles.map((a) => (
                  <Link
                    key={a.id}
                    to={"/article/" + a.id}
                    className="group bg-white rounded-xl overflow-hidden border border-background-200 hover:border-primary-300 transition-all"
                  >
                    <div className="aspect-[16/10] overflow-hidden">
                      <img
                        src={a.imageUrl}
                        alt={a.title}
                        className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <div className="p-4">
                      <span className="text-xs text-accent-600 font-medium">{a.category}</span>
                      <h4 className="mt-1 text-sm font-semibold text-foreground-950 group-hover:text-primary-500 transition-colors line-clamp-2">
                        {a.title}
                      </h4>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>

          <section className="px-4 md:px-6 py-8 md:py-12">
            <div className="max-w-4xl mx-auto">
              <h3 className="text-xl font-bold text-foreground-950 mb-6">
                相关漫画推荐
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-5">
                {relatedComics.map((comic) => (
                  <div key={comic.id} className="group cursor-pointer">
                    <div className="relative rounded-xl overflow-hidden aspect-[3/4] mb-3">
                      <img
                        src={comic.imageUrl}
                        alt={comic.title}
                        title={comic.title + " - 在线漫画韩漫"}
                        className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <h4 className="text-sm font-semibold text-foreground-950 group-hover:text-primary-500 transition-colors line-clamp-2">
                      {comic.title}
                    </h4>
                    <p className="mt-1 text-xs text-foreground-500">{comic.author}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}
